/**
 * @file Shows the last 50 authentications made with an organization public key.
 */

import React, { useMemo } from "react";
import { Typography } from "antd";

import Table, { DESCEND } from "@components/Table";
import { indexOrganizationPublicKeyAuthenticationsOperation as indexOperation } from "@api/services/identity";

import authenticationsColumnsConfig from "./helpers/authenticationsColumnsConfig";

const LIMIT = 50;
const LABEL_EMPTY = "No authentications";
const DESCRIPTION = "The table holds the last 50 authentications of this key. A refused record holds the condition that refused the assertion.";

const TABLE_SCROLL_Y = "calc(100vh - 420px)";

type Props = {
  keyId: string;
};

/**
 * Renders the table of the key's most recent authentications.
 */
const OrganizationPublicKeyAuthenticationsTable = ({
  keyId,
}: Props): JSX.Element => {
  // NOTE: the index asks for the key, and a page without a limit reads each
  //       record of the key. The request stops after one page when a limit
  //       is set, therefore the table gets the last 50 records only.
  const operationParameters = useMemo(() => ({
    limit: LIMIT,
    keyId,
  }), [ keyId ]);

  const tableProps = {
    size: "small" as const,
    locale: { emptyText: LABEL_EMPTY },
    scroll: { y: TABLE_SCROLL_Y },
    columnsConfig: authenticationsColumnsConfig,
    indexOperation,
    defaultSortColumn: "createdAt",
    operationParameters,
    defaultSortColumnDirection: DESCEND,
  };

  return (
    <>
      <Typography.Paragraph type="secondary">
        {DESCRIPTION}
      </Typography.Paragraph>

      <Table {...tableProps} />
    </>
  );
};

export default OrganizationPublicKeyAuthenticationsTable;
